import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";
import { Reveal } from "@/components/Reveal";

const S = ({ num, title, children }) => (
  <section className="mt-10" data-testid={`popia-section-${num}`}>
    <h2 className="font-display text-lg md:text-xl font-bold text-navy">
      {num}. {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ id, title, items }) => (
  <div>
    <h3 className="font-bold text-navy text-sm md:text-base">
      {id} {title}
    </h3>
    <ul className="list-disc pl-5 mt-1.5 space-y-1">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  </div>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="privacy-page">
      <Seo
        title="Privacy Policy (POPIA)"
        description="How BTMEDU collects, uses, protects and retains your personal information in terms of the Protection of Personal Information Act 4 of 2013 (POPIA)."
      />
      <BackButton to="/" label="Back to home" testId="privacy-back-button" />
      <Reveal>
        <header className="mt-6 border-b border-slate-200 pb-8">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-navy tracking-tight">
            BTMEDU (PTY) LTD
          </h1>
          <p className="mt-2 font-display text-lg md:text-xl font-bold text-brand">
            POPIA Privacy and Personal Information Protection Policy
          </p>
          <dl className="mt-4 text-xs md:text-sm text-slate-500 space-y-1">
            <div>Company Registration Number: 2018/531458/07</div>
            <div>Effective Date: 13 October 2026</div>
            <div>
              Website:{" "}
              <a href="https://www.btmedu.co.za" className="text-brand font-semibold hover:underline">
                www.btmedu.co.za
              </a>
            </div>
          </dl>
        </header>

        <S num="1" title="Purpose">
          <p>
            BTMEDU (PTY) LTD (“BTMEDU”, “the Company”, “we”, “us” or “our”) is committed to protecting personal
            information and respecting the privacy rights of customers, prospective customers, employees, suppliers,
            delivery recipients and other individuals whose personal information it processes. This policy establishes
            the Company's framework for collecting, receiving, recording, storing, using, sharing, retaining and
            disposing of personal information in accordance with the Protection of Personal Information Act 4 of 2013
            (“POPIA”), its applicable regulations and other relevant South African legislation. The purpose of this
            policy is to ensure that personal information is processed lawfully, fairly, securely and only for
            legitimate business purposes.
          </p>
        </S>

        <S num="2" title="Legislative framework">
          <p>This policy is intended to operate in accordance with:</p>
          <Ul
            items={[
              "The Protection of Personal Information Act 4 of 2013 (POPIA).",
              "The Promotion of Access to Information Act 2 of 2000 (PAIA), where applicable.",
              "The Consumer Protection Act 68 of 2008, where applicable.",
              "The Electronic Communications and Transactions Act 25 of 2002, where applicable.",
              "Other applicable South African laws governing commercial transactions, employment, taxation, accounting and recordkeeping.",
            ]}
          />
          <p>
            Where legislation imposes a specific requirement that differs from this policy, the applicable legal
            requirement will prevail.
          </p>
        </S>

        <S num="3" title="Scope and application">
          <p>This policy applies to all personal information processed by or on behalf of BTMEDU, including information collected through:</p>
          <Ul
            items={[
              "The Company's website and Progressive Web App (PWA).",
              "Online customer registrations and accounts.",
              "Product enquiries and customer support.",
              "Orders for stationery, textbooks, religious books, art and craft products, educational toys and corporate gifts.",
              "School-specific stationery and textbook lists.",
              "Shopping carts, checkout and payment processing.",
              "Delivery and collection arrangements.",
              "Stationery Stokvel enrolment and related transactions, where offered.",
              "Email, telephone, messaging platforms and other communication channels.",
              "Supplier, employee and business administration processes.",
            ]}
          />
          <p>
            This policy applies to employees, contractors and other authorised persons who process personal information
            under the Company's authority.
          </p>
        </S>

        <S num="4" title="Definitions">
          <Ul
            items={[
              "Personal information: Information relating to an identifiable living natural person and, where applicable, an identifiable existing juristic person, as defined by POPIA.",
              "Data subject: The person or entity to whom personal information relates.",
              "Responsible party: The person or organisation that determines the purpose of and means for processing personal information. BTMEDU will act as the responsible party where it determines those purposes and means.",
              "Operator: A third party that processes personal information for BTMEDU under a contract or mandate, without coming under the direct authority of the Company in doing so.",
              "Processing: Any operation involving personal information, including collection, storage, use, disclosure, alteration, retrieval or destruction.",
              "Special personal information: Information falling within the special categories protected by POPIA, including certain information relating to religious beliefs, health, biometrics and other protected characteristics.",
            ]}
          />
        </S>

        <S num="5" title="Personal information collected">
          <p>
            BTMEDU may collect and process the following categories of information where reasonably necessary for its
            business activities.
          </p>
          <Sub
            id="5.1"
            title="Customer identification and contact information"
            items={[
              "Full name and surname.",
              "Email address and telephone number.",
              "Billing and delivery addresses.",
              "Customer account identifiers.",
              "Business name and business contact details where applicable.",
            ]}
          />
          <Sub
            id="5.2"
            title="Order and transaction information"
            items={[
              "Products purchased or requested.",
              "Product quantities, prices, discounts and order totals.",
              "Order numbers and transaction references.",
              "Payment confirmation and transaction status.",
              "Delivery or collection instructions.",
              "Returns, refunds, complaints and customer support records.",
            ]}
          />
          <Sub
            id="5.3"
            title="Account and technical information"
            items={[
              "Account credentials and authentication records.",
              "Website and PWA interaction records.",
              "IP address, browser type, device information and security logs, where collected.",
              "Cookie or similar technology information, subject to applicable requirements and user choices.",
              "Passwords must be protected using appropriate security measures, including secure password hashing where passwords are managed by the Company's systems.",
            ]}
          />
          <div>
            <h3 className="font-bold text-navy text-sm md:text-base">5.4 School-related information</h3>
            <p className="mt-1.5">
              Where customers use school-specific stationery or textbook lists, BTMEDU may process:
            </p>
            <Ul
              items={[
                "School name and selected grade.",
                "Required stationery and textbook lists.",
                "Quantities selected for purchase.",
                "Information needed to associate an order with a school list.",
              ]}
            />
            <p>
              BTMEDU should not collect a child's full name, identity number, date of birth or other identifying
              information unless it is genuinely necessary for a defined purpose and there is a lawful basis to process
              it.
            </p>
          </div>
          <Sub
            id="5.5"
            title="Stokvel-related information"
            items={[
              "Where Stationery Stokvel services are offered, BTMEDU may process information required to administer enrolment, contributions, transaction records, customer balances or benefits, and eligible purchases. Financial information must be restricted to authorised personnel and protected against unauthorised access.",
            ]}
          />
          <Sub
            id="5.6"
            title="Supplier and employee information"
            items={[
              "The Company may process identification, contact, employment, contractual, banking and tax-related information where necessary for lawful business administration, employment, payment, compliance and recordkeeping.",
            ]}
          />
        </S>

        <S num="6" title="Purposes and lawful basis for processing">
          <p>BTMEDU will process personal information only where a lawful justification exists under POPIA. Purposes may include:</p>
          <Ul
            items={[
              "Creating and maintaining customer accounts.",
              "Processing orders and payments.",
              "Delivering products or arranging collection.",
              "Providing customer service and responding to enquiries.",
              "Managing returns, refunds, complaints and disputes.",
              "Administering school lists and customer-selected stationery requirements.",
              "Administering approved Stationery Stokvel services.",
              "Preventing fraud and protecting the Company's systems.",
              "Meeting applicable legal, tax, accounting and recordkeeping obligations.",
              "Sending service-related communications.",
              "Sending direct marketing where permitted by law.",
              "Improving website functionality, usability and security.",
              "Establishing, exercising or defending legal rights.",
            ]}
          />
          <p>
            Depending on the circumstances, processing may be necessary for contractual performance, compliance with a
            legal obligation, protection of legitimate interests, consent or another lawful justification recognised by
            POPIA. BTMEDU will not rely on consent where it is not the appropriate lawful basis, and will not use
            personal information for unrelated purposes without a lawful justification.
          </p>
        </S>

        <S num="7" title="Collection and notice">
          <p>BTMEDU will take reasonable steps to ensure that individuals are informed, where required, about:</p>
          <Ul
            items={[
              "The identity and contact details of the Company.",
              "The categories of information being collected.",
              "The purposes for which the information will be used.",
              "Whether the information is required or optional.",
              "The consequences of not providing information that is necessary for a transaction.",
              "The recipients or categories of recipients to whom information may be disclosed.",
              "Any relevant cross-border processing.",
              "The rights available to data subjects under POPIA.",
              "The process for lodging complaints with the Information Regulator.",
            ]}
          />
          <p>
            Personal information should, where reasonably practicable, be collected directly from the data subject.
            Information obtained from another source must be handled in accordance with applicable legal requirements.
            BTMEDU will not require customers to provide information that is unnecessary for the relevant transaction
            or service.
          </p>
        </S>

        <S num="8" title="Children's personal information">
          <p>
            BTMEDU recognises that school stationery and textbook purchases may involve children. The Company will take
            reasonable steps to minimise the collection of children's personal information. School lists and grade
            selections should generally be managed without identifying individual learners. Where BTMEDU processes
            personal information relating to a child, it will comply with the special requirements in POPIA governing
            children's information, including obtaining authorisation from a competent person where required, unless a
            lawful exception applies. BTMEDU will not assume that a parent's purchase of school supplies automatically
            authorises every possible use of a child's personal information.
          </p>
        </S>

        <S num="9" title="Special personal information">
          <p>
            BTMEDU does not intend to collect special personal information unless it is necessary for a legitimate,
            defined purpose and the processing is permitted under POPIA. If customers purchase religious books, the
            Company will ordinarily process only the information necessary to fulfil the order. A purchase must not
            automatically be used to infer, profile or record a customer's religious beliefs. Any processing of special
            personal information must be assessed separately and authorised only where a lawful basis exists.
          </p>
        </S>

        <S num="10" title="Payment information">
          <p>BTMEDU may use third-party payment service providers, including PayFast, where configured and supported. The Company will:</p>
          <Ul
            items={[
              "Use secure payment integrations and appropriate server-side verification.",
              "Process payment references, amounts and payment statuses as necessary.",
              "Avoid storing full payment card details or security codes on its own systems.",
              "Restrict access to financial transaction records.",
              "Use payment information only for legitimate transaction, reconciliation, fraud prevention, support and legal purposes.",
              "Ensure that payment providers are subject to appropriate contractual and security requirements.",
            ]}
          />
          <p>
            A payment notification or redirect to the website will not, by itself, be treated as conclusive proof that
            a payment succeeded.
          </p>
        </S>

        <S num="11" title="Disclosure to third parties">
          <p>BTMEDU may disclose personal information to appropriate third parties where necessary and lawful, including:</p>
          <Ul
            items={[
              "Payment service providers.",
              "Delivery companies and courier services.",
              "Website hosting, cloud infrastructure and software providers.",
              "Customer communication and support providers.",
              "Accounting, legal, audit and professional advisers.",
              "Regulators, law enforcement authorities or courts where legally required.",
              "Other service providers necessary to fulfil a customer request or operate the business lawfully.",
            ]}
          />
          <p>
            BTMEDU will limit disclosures to information reasonably necessary for the relevant purpose. Where an
            operator processes personal information on behalf of BTMEDU, the Company will take reasonable steps to
            ensure that an appropriate written agreement governs confidentiality and security obligations, as required
            by POPIA. Third parties receiving information for their own independent purposes must be assessed according
            to the nature of the disclosure and their applicable legal responsibilities.
          </p>
        </S>

        <S num="12" title="Cross-border transfers">
          <p>
            Where BTMEDU uses hosting, cloud, analytics, communications or other services that involve personal
            information being transferred outside South Africa, the Company will assess whether the transfer complies
            with section 72 of POPIA. Personal information will not be transferred across borders unless the applicable
            statutory requirements are met, including any required protections, agreements, authorisation or other
            lawful basis. The Company will take reasonable steps to understand where its service providers store and
            process personal information.
          </p>
        </S>

        <S num="13" title="Information security">
          <p>
            BTMEDU will implement appropriate, reasonable technical and organisational safeguards to protect personal
            information against loss, unauthorised access, unlawful processing, alteration, disclosure or destruction.
            Depending on the systems and risks involved, safeguards may include:
          </p>
          <Ul
            items={[
              "Access controls and role-based permissions.",
              "Strong authentication for privileged accounts.",
              "Encryption in transit and appropriate encryption at rest.",
              "Secure password storage and management.",
              "Security updates and vulnerability management.",
              "Firewalls and monitoring of relevant security events.",
              "Backups and recovery procedures.",
              "Restrictions on exporting customer records.",
              "Staff confidentiality obligations and training.",
              "Secure disposal of paper and electronic records.",
              "Periodic reviews of risks and the effectiveness of security measures.",
            ]}
          />
          <p>
            Access to personal information must be limited to persons who need it for authorised duties. All personnel
            must report suspected unauthorised access, loss, disclosure or misuse of personal information promptly
            through the Company's designated incident-reporting process.
          </p>
        </S>

        <S num="14" title="Security compromises and data breaches">
          <p>
            If BTMEDU has reasonable grounds to believe that personal information has been accessed or acquired by an
            unauthorised person, it will follow the incident response process required by POPIA. The Company will:
          </p>
          <Ul
            items={[
              "Contain and investigate the incident.",
              "Preserve relevant evidence and assess the nature and scope of the compromise.",
              "Take reasonable steps to prevent further unauthorised access.",
              "Notify the Information Regulator and affected data subjects as required by section 22 of POPIA, as soon as reasonably possible, subject to applicable legal provisions.",
              "Provide information required for affected persons to take protective measures.",
              "Document the incident and corrective actions.",
              "Review and improve security measures where necessary.",
            ]}
          />
          <p>
            An operator processing information for BTMEDU must notify the Company immediately if it has reasonable
            grounds to believe that personal information has been accessed or acquired by an unauthorised person.
          </p>
        </S>

        <S num="15" title="Retention and destruction">
          <p>
            BTMEDU will retain personal information only for as long as necessary for the purpose for which it was
            collected or subsequently processed, unless longer retention is required or permitted by law. Retention
            periods may depend on:
          </p>
          <Ul
            items={[
              "The completion of an order or customer relationship.",
              "Tax, accounting and statutory recordkeeping obligations.",
              "The management of refunds, warranties, complaints or disputes.",
              "Fraud prevention and security requirements.",
              "The establishment, exercise or defence of legal claims.",
              "Other applicable legal requirements.",
            ]}
          />
          <p>
            When personal information is no longer required, BTMEDU will take reasonable steps to delete, destroy or
            de-identify it securely, subject to any applicable retention obligation or lawful basis for continued
            retention. The Company will maintain a retention schedule specifying the applicable periods for key
            categories of records.
          </p>
        </S>

        <S num="16" title="Direct marketing and customer communications">
          <p>
            BTMEDU may send communications necessary to confirm orders, verify transactions, provide delivery updates,
            respond to enquiries or administer customer accounts. Promotional emails, SMS messages and other direct
            marketing communications will be handled in accordance with POPIA and other applicable laws. Where consent
            or another statutory condition is required, BTMEDU will obtain and record it before sending the relevant
            marketing communication. The Company will provide an appropriate method for customers to opt out of
            marketing communications and will honour valid opt-out requests. A customer's purchase or registration will
            not automatically be treated as unrestricted permission to send promotional communications through every
            channel.
          </p>
        </S>

        <S num="17" title="Cookies and website technologies">
          <p>The BTMEDU website and PWA may use cookies, local storage and similar technologies for purposes such as:</p>
          <Ul
            items={[
              "Maintaining sessions and authentication.",
              "Supporting cart functionality.",
              "Improving security.",
              "Remembering appropriate user preferences.",
              "Measuring website performance, where configured.",
            ]}
          />
          <p>
            Non-essential analytics or advertising technologies will be assessed for applicable privacy and consent
            requirements. BTMEDU will not store sensitive payment information or confidential customer records in
            insecure browser storage. Information stored locally must be limited to what is appropriate for the
            functionality and protected against foreseeable risks.
          </p>
        </S>

        <S num="18" title="Rights of data subjects">
          <p>Subject to applicable legal conditions and limitations, data subjects may have the right to:</p>
          <Ul
            items={[
              "Be notified that their personal information is being collected or has been accessed by an unauthorised person.",
              "Request access to their personal information.",
              "Request correction or deletion of inaccurate, irrelevant, excessive, outdated, incomplete, misleading or unlawfully obtained information.",
              "Object to certain processing on reasonable grounds relating to their particular situation.",
              "Object to direct marketing where the law provides that right.",
              "Withdraw consent where processing is based on consent.",
              "Request that certain decisions based solely on automated processing be addressed in accordance with POPIA.",
              "Lodge a complaint with the Information Regulator.",
              "Seek remedies available under applicable law.",
            ]}
          />
          <p>
            Requests will be assessed and handled within the applicable legal timeframes. BTMEDU may take reasonable
            steps to verify the identity and authority of a person making a request. A request for deletion may be
            limited where the Company is legally required or otherwise lawfully entitled to retain the information.
          </p>
        </S>

        <S num="19" title="Information Officer">
          <p>
            BTMEDU will ensure that its Information Officer and any Deputy Information Officers are appointed and
            registered with the Information Regulator where required. The Information Officer is responsible for
            overseeing the Company's POPIA compliance framework, including relevant policies, training, internal
            procedures, cooperation with the Information Regulator and the handling of privacy-related requests and
            complaints.
          </p>
          <Ul
            items={[
              "Information Officer: Tshegofatso Phaloane",
              "Designation: Director",
              "Email: info@btmedu.co.za",
              "Telephone: 061 081 4598",
              "Business address: Shop 16 North Park Mall, 256 Burger Street, Pretoria North",
            ]}
          />
          <p>
            Privacy-related requests and complaints may be directed to the Information Officer at{" "}
            <a href="mailto:info@btmedu.co.za" className="text-brand font-semibold hover:underline">
              info@btmedu.co.za
            </a>{" "}
            or on 061 081 4598.
          </p>
        </S>

        <S num="20" title="Complaints and escalation">
          <p>
            A person who has concerns about BTMEDU's handling of personal information may first contact the Company's
            Information Officer using the verified details published on the website. The Company will record, assess
            and respond to complaints in accordance with applicable requirements. Data subjects may also approach the
            Information Regulator of South Africa through its official channels:
          </p>
          <p>
            Website:{" "}
            <a
              href="https://inforegulator.org.za/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand font-semibold hover:underline"
            >
              https://inforegulator.org.za/
            </a>
          </p>
          <p>This policy does not limit any statutory rights or remedies available to a data subject.</p>
        </S>

        <S num="21" title="Employee and contractor responsibilities">
          <p>Employees, contractors and authorised service providers must:</p>
          <Ul
            items={[
              "Access personal information only for authorised purposes.",
              "Keep information confidential.",
              "Follow approved security and data-handling procedures.",
              "Avoid sharing customer information through unauthorised channels.",
              "Report suspected incidents immediately.",
              "Complete relevant privacy and information-security training.",
              "Return or securely dispose of information when their authorised access ends, subject to lawful recordkeeping requirements.",
            ]}
          />
          <p>
            Unauthorised access, disclosure or misuse may result in disciplinary action, termination of access or
            contractual remedies, and may be referred to the appropriate authorities where warranted.
          </p>
        </S>

        <S num="22" title="Governance and compliance monitoring">
          <p>BTMEDU will take reasonable steps to maintain an ongoing privacy compliance programme, which may include:</p>
          <Ul
            items={[
              "Maintaining a record of processing activities.",
              "Assessing privacy risks associated with new systems and services.",
              "Reviewing operator and service-provider agreements.",
              "Reviewing access permissions and security controls.",
              "Training relevant personnel.",
              "Maintaining incident and request registers.",
              "Reviewing retention periods.",
              "Assessing cross-border data transfers.",
              "Reviewing this policy at least annually and when material changes occur.",
            ]}
          />
          <p>
            This policy must be read together with the Company's applicable privacy notice, information security
            procedures, retention schedule, operator agreements and PAIA manual.
          </p>
        </S>

        <S num="23" title="Amendments">
          <p>
            BTMEDU may amend this policy when required by changes in legislation, business operations, technology,
            service providers or identified risks. Material changes will be communicated through appropriate channels
            where required by law. The current version will be made available through the Company's website or another
            appropriate publication channel.
          </p>
        </S>

        <S num="24" title="Approval and adoption">
          <p>This policy is adopted by BTMEDU (PTY) LTD.</p>
          <p className="font-semibold text-navy">Directors</p>
        </S>

        <div className="mt-12 rounded-2xl bg-cream border border-slate-200 p-6 text-sm text-slate-600">
          <p>
            Questions about this policy? Contact us at{" "}
            <a href="mailto:orders@btmedu.co.za" className="text-brand font-semibold hover:underline">
              orders@btmedu.co.za
            </a>{" "}
            or see our <Link to="/terms" className="text-brand font-semibold hover:underline" data-testid="privacy-terms-link">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </Reveal>
    </div>
  );
}
